import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import {
  Shield,
  Home,
  ArrowRight,
  CheckCircle2,
  Lock,
  Users,
  Flame,
  Quote,
  Package,
  LineChart,
  LifeBuoy
} from 'lucide-react'
import HeroSlider from '../components/HeroSlider'
import FeatureCard from '../components/FeatureCard'
import StatsSection from '../components/StatsSection'
import CTASection from '../components/CTASection'
import ScramblerShowcase from '../components/ScramblerShowcase'
import AIOpsSuiteBand from '../components/AIOpsSuiteBand'

const features = [
  {
    icon: Package,
    title: 'An Entry Point',
    description: 'One SD card, one small unit. Flash it, boot it — you\'re running governed AI without a data center or an engineering team.',
    href: '/products/home',
  },
  {
    icon: Shield,
    title: 'A Security Feature',
    description: 'A compliance rulebook and audit trail built in, not bolted on — an accountable identity behind every unit, every action.',
    href: '/products/governance',
  },
  {
    icon: LineChart,
    title: 'A Conductor',
    description: 'Governed retrieval and multi-model workflows, orchestrated — swap model providers without re-architecting your pipeline.',
    href: '/ai-operations',
  },
  {
    icon: LifeBuoy,
    title: "Real People You Can Reach",
    description: "We're a small, hands-on team — not a call center. If you need help, we want you to feel like you can count on us.",
    href: '/support',
  },
]

const useCases = [
  {
    icon: Home,
    title: 'New Here?',
    description: 'Get a Scrambler, build your AI setup the way you want it, and power it with ready-made models that run safely on your own hardware.',
    features: ['Device specs & pricing', 'What ships in the box', 'Requires a 23Controls™ account & Identity Anchor'],
    href: '/products/devices/graffiti-scrambler',
    gradient: 'from-signal-500 to-synth-600',
  },
  {
    icon: LifeBuoy,
    title: 'Already With Us?',
    description: "Sign in to your account, track an order, review the latest terms or disclosures, or just get help. We're a small team — you can count on reaching a real person.",
    features: ['Account & order tracking', 'Terms, privacy & disclosures', 'Support & consultation'],
    href: '/login',
    gradient: 'from-synth-500 to-synth-700',
  },
]

export default function HomePage() {
  return (
    <div className="overflow-hidden">
      {/* Hero Slider */}
      <HeroSlider />
      
      {/* Philosophy Mission Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-void-950 via-ember-950/10 to-void-950" />
        
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-ember-900/30 border border-ember-700/30 text-ember-400 text-sm font-medium mb-6">
              <Flame className="w-4 h-4" />
              Our Governing Philosophy
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-void-50 mb-6">
              AI for <span className="text-gradient">Everyone</span>, Everywhere
            </h2>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid lg:grid-cols-2 gap-8 items-center"
          >
            {/* Quote Block */}
            <div className="p-8 rounded-3xl bg-void-900/50 border border-void-800/50 relative">
              <Quote className="absolute top-6 right-6 w-12 h-12 text-synth-900/50" />
              <p className="text-lg md:text-xl text-void-200 leading-relaxed mb-6 relative z-10">
                Graffiti™ is this world's oldest of enduring means of communication. Long before 
                Sumer or Persia, the Toltecs or the 'New World'—humans have left messages which 
                will exist beyond their speaking, to the generations.
              </p>
              <p className="text-void-400 leading-relaxed">
                We named our technology <span className="text-synth-400 font-semibold">Graffiti™</span> because 
                we want to empower our generation to grow a society comfortable with AI—using our 
                models to craft ideas, build visions, solve problems, and tell family stories.
              </p>
              <Link 
                to="/philosophy" 
                className="inline-flex items-center gap-2 mt-6 text-synth-400 hover:text-synth-300 transition-colors font-medium"
              >
                Read Our Full Philosophy
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            
            {/* Core Principles */}
            <div className="space-y-4">
              <div className="p-5 rounded-2xl bg-void-900/30 border border-void-800/50 flex items-start gap-4">
                <div className="p-2.5 rounded-xl bg-synth-900/50 border border-synth-700/30">
                  <Users className="w-5 h-5 text-synth-400" />
                </div>
                <div>
                  <h4 className="font-display font-semibold text-void-100 mb-1">Universal Access</h4>
                  <p className="text-sm text-void-400">
                    Powerful AI features for people of ANY socioeconomic class—inexpensively, safely, privately.
                  </p>
                </div>
              </div>
              
              <div className="p-5 rounded-2xl bg-void-900/30 border border-void-800/50 flex items-start gap-4">
                <div className="p-2.5 rounded-xl bg-signal-900/50 border border-signal-700/30">
                  <Lock className="w-5 h-5 text-signal-400" />
                </div>
                <div>
                  <h4 className="font-display font-semibold text-void-100 mb-1">Zero Data Export</h4>
                  <p className="text-sm text-void-400">
                    No transfer of personal, health, or financial data to ANY third party. Your data stays yours.
                  </p>
                </div>
              </div>
              
              <div className="p-5 rounded-2xl bg-void-900/30 border border-void-800/50 flex items-start gap-4">
                <div className="p-2.5 rounded-xl bg-ember-900/50 border border-ember-700/30">
                  <Shield className="w-5 h-5 text-ember-400" />
                </div>
                <div>
                  <h4 className="font-display font-semibold text-void-100 mb-1">Ethical & Open</h4>
                  <p className="text-sm text-void-400">
                    Open-source foundations with responsible profitability and transparent compliance.
                  </p>
                </div>
              </div>
              
              <div className="p-4 rounded-xl bg-gradient-to-r from-synth-900/30 to-signal-900/30 border border-synth-800/30">
                <p className="text-sm text-void-300 text-center">
                  <span className="font-semibold text-void-100">Graffiti™</span> lives safely inside your homes 
                  or companies—a FIRST user-experience in AI adoption for anyone, anywhere, any time.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* Graffiti™ Scrambler Product Showcase */}
      <ScramblerShowcase />

      {/* AI Operations Suite (Conductor) feature band */}
      <AIOpsSuiteBand />
      
      {/* Features Section */}
      <section className="py-24 relative">
        <div className="absolute inset-0 grid-overlay" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title mb-4">
              An Entry Point, a <span className="text-gradient">Security Feature</span>
            </h2>
            <p className="section-subtitle mx-auto">
              Graffiti™ and 23Controls™ are built to be an on-ramp into governed AI, not
              another platform to manage.
            </p>
          </motion.div>
          
          {/* Features Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <FeatureCard 
                key={feature.title} 
                {...feature} 
                index={index}
              />
            ))}
          </div>
        </div>
      </section>
      
      {/* Stats Section */}
      <StatsSection />
      
      {/* Use Cases Section */}
      <section className="py-24 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-void-950 via-void-900/50 to-void-950" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title mb-4">
              New Here, Or <span className="text-gradient">Coming Back?</span>
            </h2>
            <p className="section-subtitle mx-auto">
              Most visits are one of two things.
            </p>
          </motion.div>

          {/* Use Cases Grid */}
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {useCases.map((useCase, index) => (
              <motion.div
                key={useCase.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Link
                  to={useCase.href}
                  className="group block h-full p-8 rounded-3xl bg-void-900/30 border border-void-800/50 hover:border-synth-700/50 transition-all duration-300 hover:-translate-y-1"
                >
                  {/* Icon */}
                  <div className={`inline-flex p-4 rounded-2xl bg-gradient-to-br ${useCase.gradient} mb-6`}>
                    <useCase.icon className="w-8 h-8 text-white" />
                  </div>
                  
                  {/* Content */}
                  <h3 className="text-2xl font-display font-bold text-void-50 mb-3 group-hover:text-synth-300 transition-colors">
                    {useCase.title}
                  </h3>
                  <p className="text-void-400 mb-6 leading-relaxed">
                    {useCase.description}
                  </p>
                  
                  {/* Features list */}
                  <ul className="space-y-2 mb-6">
                    {useCase.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-2 text-void-300">
                        <CheckCircle2 className="w-4 h-4 text-synth-500" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  {/* CTA */}
                  <span className="inline-flex items-center gap-2 text-synth-400 font-medium group-hover:gap-3 transition-all">
                    Learn More
                    <ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* "Meet Graffiti" model showcase removed: it claimed a flagship LLM
          with invented capabilities ("code generation across 50+ languages",
          a fake 127ms/1,247-token API response) that doesn't correspond to
          any shipped product — Graffiti™ is the OS/governance framework
          running on the Scrambler, not a hosted language model with its own
          API. Re-add only if that product is real and the claims are
          sourced. */}

      {/* Testimonials removed: the three quotes were fabricated (invented
          names, companies, and metrics) with no real customers to attribute
          them to — the company is pilot-stage per its own executive
          materials. Re-add once there are real, attributable testimonials. */}

      {/* CTA Section */}
      <CTASection />
    </div>
  )
}